// src/components/dashboard/MyTasksSectioned.jsx
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Chip,
  Stack,
  CircularProgress,
  useTheme,
  Badge,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText
} from '@mui/material';
import {
  Schedule as ScheduleIcon,
  Warning as WarningIcon,
  FilterList as FilterIcon,
  Business as BusinessIcon,
  Timer as TimerIcon,
  PlayArrow as PlayArrowIcon,
  Stop as StopIcon
} from '@mui/icons-material';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import { useSnackbar } from 'notistack';
import { format, isAfter, isBefore, isToday, addDays } from 'date-fns';
import confetti from 'canvas-confetti';

// Components
import UnifiedTaskCard from '../tasks/UnifiedTaskCard';
import MagicSelect from '../ui/FormFields/MagicSelect';
import ViewAsSelector from './ViewAsSelector';

// Services & Hooks
import { getMyTasks, patchTask, getMyTasksAsUser, updateMyTasksOrderAsUser } from '../../services/taskService';
import { getProjects } from '../../services/projectService';
import { startTimer, stopTimer } from '../../services/timeTrackingService';
import useAuthStore from '../../stores/authStore';
import useSettingsStore from '../../stores/settingsStore';
import useTaskStore from '../../stores/taskStore';
import { useSocketEvent } from '../../hooks/useSocketEvent';

// Constants
const TASKS_PER_SECTION = 5;

// Helper function to map status to section
const mapStatusToSection = (status) => {
  const statusMap = {
    'to_do': 'pending',
    'in_progress': 'inProgress',
    'done': 'completed',
    'cancelled': 'completed',  // Cancelled tasks also go to completed
    'in_review': 'inProgress'  // In review tasks stay in progress
  };
  return statusMap[status] || 'pending';
};

const ENCOURAGING_MESSAGES = {
  pending: [
    "No pending tasks! 🎉 You're all caught up!",
    "Inbox zero achieved! Time for a coffee break ☕",
    "Nothing pending - you're on fire! 🔥",
    "All clear! Your to-do list is empty 🚀",
    "No tasks waiting - productivity champion! 🏆",
    "Clean slate! Ready for new challenges 💪",
    "Nothing to do? Enjoy the moment! 🌟",
    "Task-free zone! You're doing great! ✨",
    "No pending work - time to relax! 🎯",
    "All caught up! What's next? 🌈"
  ],
  inProgress: [
    "No work in progress - ready for new challenges! 💪",
    "Clean slate! Pick your next adventure 🎯",
    "Nothing in progress - time to start something new! ✨",
    "All quiet on the work front 🌟",
    "Ready for action - no tasks in flight! 🛫",
    "No active tasks - choose your next mission! 🚀",
    "Clear runway - ready for takeoff! ✈️",
    "Nothing cooking - time to start! 🍳",
    "No tasks running - what's next? 🎨",
    "Ready to roll - pick a task! 🎲"
  ],
  completed: [
    "No recent completions - let's change that! 💫",
    "Start your winning streak today! 🌈",
    "Ready to accomplish great things! 🎨",
    "Your completed list awaits new victories! 🏅",
    "Time to create some success stories! 📚",
    "No completions yet - let's make it happen! 🌟",
    "Ready to check off some tasks! ✅",
    "Time to build that completed list! 🏗️",
    "Let's get some wins on the board! 🎯",
    "Your success story starts now! 🚀"
  ]
};

const MyTasksSectioned = () => {
  const theme = useTheme();
  const { user, hasPermission } = useAuthStore();
  const { settings, updateSettings } = useSettingsStore();
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  // Socket is no longer needed - using useSocketEvent hooks instead
  const { openTaskDrawer, currentTask } = useTaskStore();
  
  // Dashboard preferences
  const dashboardPrefs = settings?.preferences?.dashboard || {};
  
  const [sections, setSections] = useState({
    pending: {
      id: 'pending',
      title: 'Pending',
      color: theme.palette.warning.main,
      tasks: [],
      page: 1,
      hasMore: false,
      total: 0,
      loading: false
    },
    inProgress: {
      id: 'inProgress',
      title: 'In Progress',
      color: theme.palette.info.main,
      tasks: [],
      page: 1,
      hasMore: false,
      total: 0,
      loading: false
    },
    completed: {
      id: 'completed',
      title: 'Completed (Last 30 days)',
      color: theme.palette.success.main,
      tasks: [],
      page: 1,
      hasMore: false,
      total: 0,
      loading: false
    }
  });
  
  const [initialLoading, setInitialLoading] = useState(true);
  const [isDragging, setIsDragging] = useState(false);
  const [selectedProjectId, setSelectedProjectId] = useState('all');
  const [projects, setProjects] = useState([]);
  const [timerDialog, setTimerDialog] = useState({ open: false, taskId: null, taskTitle: '', projectName: '', sectionName: '' });
  const [stopTimerDialog, setStopTimerDialog] = useState({ open: false, taskId: null, taskTitle: '', projectName: '', sectionName: '', destSection: '' });
  const [motivationalMessage, setMotivationalMessage] = useState({ show: false, text: '' });
  
  // View As feature state
  const [viewAsUserId, setViewAsUserId] = useState(null);
  const [canViewAs, setCanViewAs] = useState(false);
  const [canManageViewAs, setCanManageViewAs] = useState(false);
  
  // Check if user has VIEW_AS and VIEW_AS_MANAGE permissions
  const checkViewAsPermissions = useCallback(() => {
    // Use the hasPermission function from authStore (handles superadmin check internally)
    const canView = hasPermission('mytasks', 'view_as');
    const canManage = hasPermission('mytasks', 'view_as_manage');
    console.log('MyTasksSectioned: checkViewAsPermissions - canView:', canView, 'canManage:', canManage);
    setCanViewAs(canView);
    setCanManageViewAs(canManage);
  }, [hasPermission]);
  
  // Fetch tasks with grouping
  const fetchTasks = useCallback(async (isLoadMore = false, sectionId = null, resetPages = false) => {
    try {
      // Set loading state
      if (isLoadMore && sectionId) {
        setSections(prev => ({
          ...prev,
          [sectionId]: { ...prev[sectionId], loading: true }
        }));
      }
      
      // Get current pages from state to avoid stale closure issues
      const currentSections = resetPages ? {
        pending: { page: 1 },
        inProgress: { page: 1 },
        completed: { page: 1 }
      } : sections;
      
      const params = {
        groupByStatus: true,
        includeCollaborating: true,
        completedDaysLimit: 30,
        pendingPage: isLoadMore && sectionId === 'pending' 
          ? currentSections.pending.page + 1 
          : currentSections.pending.page,
        inProgressPage: isLoadMore && sectionId === 'inProgress' 
          ? currentSections.inProgress.page + 1 
          : currentSections.inProgress.page,
        completedPage: isLoadMore && sectionId === 'completed' 
          ? currentSections.completed.page + 1 
          : currentSections.completed.page
      };
      
      // Add project filter if not 'all'
      if (selectedProjectId !== 'all') {
        params.projectId = selectedProjectId;
      }
      
      // Use View As API if viewing another user's tasks
      console.log('MyTasksSectioned: Before API call - viewAsUserId:', viewAsUserId);
      console.log('MyTasksSectioned: Params for API:', params);
      
      const response = viewAsUserId 
        ? await getMyTasksAsUser(viewAsUserId, params)
        : await getMyTasks(params);
      
      console.log('MyTasksSectioned: API Response:', response);
      console.log('MyTasksSectioned: viewAsUserId after call:', viewAsUserId);
      
      const { sections: fetchedSections, viewAsContext } = response.data;
      
      // Update canManageViewAs based on backend response when in View As mode
      if (viewAsUserId && viewAsContext?.canManageOrdering !== undefined) {
        console.log('MyTasksSectioned: Setting canManageViewAs from backend:', viewAsContext.canManageOrdering);
        setCanManageViewAs(viewAsContext.canManageOrdering);
      } else if (!viewAsUserId) {
        // Reset to default permission check when not in View As mode
        checkViewAsPermissions();
      }
      
      setSections(prev => {
        const newSections = { ...prev };
        
        // Update each section
        Object.keys(fetchedSections).forEach(key => {
          if (resetPages) {
            // When resetting (user changed), replace all section data
            newSections[key] = {
              ...prev[key],
              tasks: fetchedSections[key].tasks,
              page: fetchedSections[key].page,
              hasMore: fetchedSections[key].hasMore,
              total: fetchedSections[key].total,
              loading: false
            };
          } else if (isLoadMore && sectionId === key) {
            // Append tasks for load more (only for the specific section)
            newSections[key] = {
              ...prev[key],
              tasks: [...prev[key].tasks, ...fetchedSections[key].tasks],
              page: fetchedSections[key].page,
              hasMore: fetchedSections[key].hasMore,
              total: fetchedSections[key].total,
              loading: false
            };
          } else if (isLoadMore && sectionId !== key) {
            // When loading more for one section, keep other sections unchanged
            // Don't update them with empty arrays from the response
            newSections[key] = prev[key];
          } else {
            // Replace tasks for initial load or refresh
            newSections[key] = {
              ...prev[key],
              tasks: fetchedSections[key].tasks,
              page: fetchedSections[key].page,
              hasMore: fetchedSections[key].hasMore,
              total: fetchedSections[key].total,
              loading: false
            };
          }
        });
        
        return newSections;
      });
      
      setInitialLoading(false);
    } catch (error) {
      console.error('Failed to fetch tasks:', error);
      enqueueSnackbar('Failed to load tasks', { variant: 'error' });
      setInitialLoading(false);
      
      if (isLoadMore && sectionId) {
        setSections(prev => ({
          ...prev,
          [sectionId]: { ...prev[sectionId], loading: false }
        }));
      }
    }
  }, [sections, enqueueSnackbar, selectedProjectId, viewAsUserId, checkViewAsPermissions]);
  
  // Initial load
  useEffect(() => {
    fetchTasks();
  }, []); // Only run once on mount
  
  // Fetch projects for filter
  useEffect(() => {
    const fetchProjects = async () => {
      try {
        const response = await getProjects();
        setProjects(response.data?.projects || response.projects || []);
      } catch (error) {
        console.error('Failed to fetch projects:', error);
      }
    };
    fetchProjects();
  }, []);
  
  // Refresh tasks when project filter changes
  useEffect(() => {
    // Skip initial render
    if (selectedProjectId === 'all' && sections.pending.tasks.length === 0 && sections.inProgress.tasks.length === 0 && sections.completed.tasks.length === 0) {
      return;
    }
    
    // Reset pages and fetch with new filter
    setSections(prev => ({
      pending: { ...prev.pending, page: 1, tasks: [], hasMore: false, total: 0 },
      inProgress: { ...prev.inProgress, page: 1, tasks: [], hasMore: false, total: 0 },
      completed: { ...prev.completed, page: 1, tasks: [], hasMore: false, total: 0 }
    }));
    fetchTasks(false, null, true);
  }, [selectedProjectId]); // eslint-disable-line react-hooks/exhaustive-deps
  
  // Listen for task updates from the drawer
  useEffect(() => {
    if (!currentTask) return;
    
    // Update the task in sections when currentTask changes
    const taskId = currentTask._id;
    
    setSections(prev => {
      const newSections = { ...prev };
      
      // Find and update the task in all sections
      Object.keys(newSections).forEach(sectionKey => {
        const taskIndex = newSections[sectionKey].tasks.findIndex(t => t._id === taskId);
        if (taskIndex !== -1) {
          // Update the task with the latest data
          newSections[sectionKey].tasks[taskIndex] = currentTask;
        }
      });
      
      return newSections;
    });
  }, [currentTask]);
  
  // Socket listeners for real-time updates using useSocketEvent hooks
  useSocketEvent('task:updated', (data) => {
    const { task, updatedBy } = data;

    // Skip if we made this change (already updated optimistically)
    if (updatedBy === user?._id) return;

    console.log('Socket: Task updated by another user', { taskId: task._id, updatedBy });

    // Granular update - update the task wherever it exists
    setSections(prev => {
        const newSections = { ...prev };
        let taskFound = false;
        let oldSection = null;
        
        // Find and update the task in all sections
        Object.keys(newSections).forEach(sectionKey => {
          const taskIndex = newSections[sectionKey].tasks.findIndex(t => t._id === task._id);
          
          if (taskIndex !== -1) {
            taskFound = true;
            oldSection = sectionKey;
            
            // Determine if task should stay in this section
            const targetUserId = viewAsUserId || user?._id;
            const isCollaborator = task.collaborators?.some(c => 
              c.user?._id === targetUserId || c.user === targetUserId
            );
            
            let taskBelongsHere = false;
            if (isCollaborator) {
              const collab = task.collaborators.find(c => 
                c.user?._id === targetUserId || c.user === targetUserId
              );
              taskBelongsHere = mapStatusToSection(collab?.status) === sectionKey;
            } else if (task.assignedTo?._id === targetUserId || task.assignedTo === targetUserId) {
              taskBelongsHere = mapStatusToSection(task.status) === sectionKey;
            }
            
            if (taskBelongsHere) {
              // Update in place
              newSections[sectionKey].tasks[taskIndex] = {
                ...newSections[sectionKey].tasks[taskIndex],
                ...task,
                myTasksOrderIndex: task.myTasksOrderIndex || newSections[sectionKey].tasks[taskIndex].myTasksOrderIndex
              };
              
              // Re-sort if order changed
              if (task.myTasksOrderIndex !== undefined) {
                newSections[sectionKey].tasks.sort((a, b) => 
                  (a.myTasksOrderIndex || 0) - (b.myTasksOrderIndex || 0)
                );
              }
            } else {
              // Remove from this section (it moved)
              newSections[sectionKey].tasks.splice(taskIndex, 1);
              newSections[sectionKey].total = Math.max(0, newSections[sectionKey].total - 1);
            }
          }
        });
        
        // If task wasn't found or was removed, check if it should be added to a section
        if (!taskFound || oldSection) {
          const targetUserId = viewAsUserId || user?._id;
          const isCollaborator = task.collaborators?.some(c => 
            c.user?._id === targetUserId || c.user === targetUserId
          );
          
          let targetSection = null;
          if (isCollaborator) {
            const collab = task.collaborators.find(c => 
              c.user?._id === targetUserId || c.user === targetUserId
            );
            targetSection = mapStatusToSection(collab?.status);
          } else if (task.assignedTo?._id === targetUserId || task.assignedTo === targetUserId) {
            targetSection = mapStatusToSection(task.status);
          }
          
          // Add to new section if needed and not already there
          if (targetSection && newSections[targetSection]) {
            const alreadyExists = newSections[targetSection].tasks.some(t => t._id === task._id);
            if (!alreadyExists) {
              newSections[targetSection].tasks.push(task);
              newSections[targetSection].tasks.sort((a, b) => 
                (a.myTasksOrderIndex || 0) - (b.myTasksOrderIndex || 0)
              );
              newSections[targetSection].total += 1;
            }
          }
        }
        
        return newSections;
    });
  });

  useSocketEvent('task:created', (data) => {
      const { task, createdBy } = data;
      
      // Skip if we created this task
      if (createdBy === user?._id) return;
      
      console.log('Socket: New task created by another user', { taskId: task._id, createdBy });
      
      setSections(prev => {
        const newSections = { ...prev };
        
        // Determine which section this task belongs in
        const targetUserId = viewAsUserId || user?._id;
        const isCollaborator = task.collaborators?.some(c => 
          c.user?._id === targetUserId || c.user === targetUserId
        );
        
        let targetSection = null;
        if (isCollaborator) {
          const collab = task.collaborators.find(c => 
            c.user?._id === targetUserId || c.user === targetUserId
          );
          targetSection = mapStatusToSection(collab?.status || 'to_do');
        } else if (task.assignedTo?._id === targetUserId || task.assignedTo === targetUserId) {
          targetSection = mapStatusToSection(task.status || 'to_do');
        }
        
        // Add to appropriate section if we're on page 1
        if (targetSection && newSections[targetSection]) {
          const sectionData = newSections[targetSection];
          
          // Only add to visible tasks if we're on page 1 or have room
          if (sectionData.page === 1 || sectionData.tasks.length < TASKS_PER_SECTION) {
            // Check if task already exists (prevent duplicates)
            if (!sectionData.tasks.some(t => t._id === task._id)) {
              sectionData.tasks.push(task);
              sectionData.tasks.sort((a, b) => 
                (a.myTasksOrderIndex || 0) - (b.myTasksOrderIndex || 0)
              );
              
              // Trim to max items per page if needed
              if (sectionData.tasks.length > TASKS_PER_SECTION * sectionData.page) {
                sectionData.tasks = sectionData.tasks.slice(0, TASKS_PER_SECTION * sectionData.page);
              }
            }
          }
          
          // Always update total count
          sectionData.total += 1;
        }
        
        return newSections;
    });
  });
  
  // Check permissions when user changes
  useEffect(() => {
    checkViewAsPermissions();
  }, [checkViewAsPermissions]);
  
  // Refetch tasks when View As user changes
  useEffect(() => {
    // Reset ALL section data completely when changing view
    setSections({
      pending: {
        id: 'pending',
        title: 'Pending',
        color: theme.palette.warning.main,
        tasks: [],
        page: 1,
        hasMore: false,
        total: 0,
        loading: false
      },
      inProgress: {
        id: 'inProgress',
        title: 'In Progress',
        color: theme.palette.info.main,
        tasks: [],
        page: 1,
        hasMore: false,
        total: 0,
        loading: false
      },
      completed: {
        id: 'completed',
        title: 'Completed (Last 30 days)',
        color: theme.palette.success.main,
        tasks: [],
        page: 1,
        hasMore: false,
        total: 0,
        loading: false
      }
    });
    // Fetch with reset flag to ensure clean state
    fetchTasks(false, null, true);
  }, [viewAsUserId]); // eslint-disable-line react-hooks/exhaustive-deps
  
  // Handle View As change
  const handleViewAsChange = useCallback((userId) => {
    console.log('MyTasksSectioned: handleViewAsChange called with userId:', userId);
    setViewAsUserId(userId);
    // Don't show notifications here - let the ViewAsSelector component handle them
  }, []);
  
  // Show in progress celebration - motivate users when starting work
  const showInProgressCelebration = useCallback(() => {
    // Show encouraging message with random motivational quotes
    const motivationalMessages = [
      '🚀 Let\'s get this done! You\'ve got this! 💪',
      '⚡ Time to make it happen! Let\'s go! 🔥',
      '💫 Great start! Keep the momentum going! 🎯',
      '🌟 You\'re on fire! Let\'s crush this task! 💪',
      '🎯 Focus mode activated! You can do this! 🚀',
      '✨ Amazing! One step closer to completion! 🏆',
      '🔥 Let\'s turn this task into a success! ⭐',
      '💪 Progress mode: ON! Keep pushing forward! 🎯'
    ];
    
    const randomMessage = motivationalMessages[Math.floor(Math.random() * motivationalMessages.length)];
    
    // Show custom motivational message
    setMotivationalMessage({ show: true, text: randomMessage });
    
    // Hide message after 4 seconds
    setTimeout(() => {
      setMotivationalMessage({ show: false, text: '' });
    }, 4000);
    
    // Trigger energizing animation with blue/purple colors
    const duration = 2 * 1000;
    const animationEnd = Date.now() + duration;
    const defaults = { startVelocity: 25, spread: 55, ticks: 40, zIndex: 9999 };
    
    const randomInRange = (min, max) => {
      return Math.random() * (max - min) + min;
    };
    
    // Single burst from bottom center going upward
    const interval = setInterval(() => {
      const timeLeft = animationEnd - Date.now();
      
      if (timeLeft <= 0) {
        return clearInterval(interval);
      }
      
      const particleCount = 30 * (timeLeft / duration);
      
      // Shoot particles upward from bottom center
      confetti({
        ...defaults,
        particleCount,
        origin: { x: 0.5, y: 0.9 },
        colors: ['#2196f3', '#3f51b5', '#7c4dff', '#536dfe', '#448aff'], // Blue and purple shades
        angle: 90,
        spread: 45,
        startVelocity: 35
      });
    }, 250);
  }, [enqueueSnackbar]);
  
  // Show completion celebration - define before handleDragEnd
  const showCompletionCelebration = useCallback(() => {
    // Show success message
    enqueueSnackbar('🎉 Task completed! Great job! 🎊', { 
      variant: 'success',
      autoHideDuration: 5000
    });
    
    // Trigger confetti animation
    const duration = 3 * 1000;
    const animationEnd = Date.now() + duration;
    const defaults = { startVelocity: 30, spread: 360, ticks: 60, zIndex: 9999 };
    
    const randomInRange = (min, max) => {
      return Math.random() * (max - min) + min;
    };
    
    const interval = setInterval(() => {
      const timeLeft = animationEnd - Date.now();
      
      if (timeLeft <= 0) {
        return clearInterval(interval);
      }
      
      const particleCount = 50 * (timeLeft / duration);
      
      // Shoot confetti from both sides
      confetti({
        ...defaults,
        particleCount,
        origin: { x: randomInRange(0.1, 0.3), y: Math.random() - 0.2 },
        colors: ['#26a69a', '#42a5f5', '#66bb6a', '#ffa726', '#ef5350']
      });
      confetti({
        ...defaults,
        particleCount,
        origin: { x: randomInRange(0.7, 0.9), y: Math.random() - 0.2 },
        colors: ['#26a69a', '#42a5f5', '#66bb6a', '#ffa726', '#ef5350']
      });
    }, 250);
  }, [enqueueSnackbar]);
  
  // Handle drag start
  const handleDragStart = useCallback((start) => {
    console.log('Dashboard: Drag started:', start);
    
    // Set global drag state
    const { setIsDragging: setGlobalDragging } = useTaskStore.getState();
    setGlobalDragging(true);
    setIsDragging(true);
  }, []);
  
  // Handle drag update
  const handleDragUpdate = useCallback((update) => {
    // Log drag updates to debug
    if (update.destination) {
      console.log('Dashboard: Drag update - over droppable:', update.destination.droppableId);
    }
  }, []);
  
  // Handle drag end
  const handleDragEnd = useCallback(async (result) => {
    console.log('Dashboard: handleDragEnd called');
    
    // Reset dragging state
    const { setIsDragging: setGlobalDragging } = useTaskStore.getState();
    setGlobalDragging(false);
    setIsDragging(false);
    
    // Clear any timeouts
    if (window._dragTimeout) {
      clearTimeout(window._dragTimeout);
      window._dragTimeout = null;
    }
    
    const { source, destination, draggableId, type } = result;
    
    // Check if dropped outside of any droppable
    if (!destination) return;
    
    // If dropped in same position, do nothing
    if (
      destination.droppableId === source.droppableId &&
      destination.index === source.index
    ) {
      return;
    }
    
    // Only handle TASK type
    if (type !== 'TASK') return;
    
    const sourceSection = source.droppableId;
    const destSection = destination.droppableId;
    const sourceIndex = source.index;
    const destIndex = destination.index;
    const taskId = draggableId;
    
    console.log('Dashboard: Drag operation details:', {
      type,
      sourceSection,
      destSection,
      taskId,
      sourceIndex,
      destIndex
    });
    
    // Store complete state snapshot for rollback
    const stateSnapshot = {
      sections: JSON.parse(JSON.stringify(sections)),
      timestamp: Date.now()
    };
    
    try {
      // Find the task being moved
      const task = sections[sourceSection]?.tasks?.find(t => t._id === taskId);
      if (!task) {
        console.error('Task not found:', taskId);
        return;
      }
      
      // Store original task properties for rollback
      const originalTaskState = {
        myTasksOrderIndex: task.myTasksOrderIndex,
        status: task.status,
        collaborators: task.collaborators ? [...task.collaborators] : []
      };
      
      // Optimistically update UI
      setSections(prev => {
        const newSections = { ...prev };
        
        // Remove from source
        const sourceTasks = [...(prev[sourceSection]?.tasks || [])];
        sourceTasks.splice(sourceIndex, 1);
        newSections[sourceSection] = {
          ...prev[sourceSection],
          tasks: sourceTasks,
          total: sourceSection === destSection ? prev[sourceSection].total : Math.max(0, prev[sourceSection].total - 1)
        };
        
        // Add to destination
        if (sourceSection !== destSection) {
          const destTasks = [...(prev[destSection]?.tasks || [])];
          destTasks.splice(destIndex, 0, task);
          newSections[destSection] = {
            ...prev[destSection],
            tasks: destTasks,
            total: prev[destSection].total + 1
          };
        } else {
          // Reordering within same section
          sourceTasks.splice(destIndex, 0, task);
        }
        
        return newSections;
      });
      
      // Calculate new myTasksOrderIndex for the moved task using fractional indexing
      const calculateMyTasksOrderIndex = (tasks, index) => {
        // When placing in empty section or as first task
        if (tasks.length === 0) return 1.0;
        
        if (index === 0) {
          // Placed at the beginning - use half of first item's index
          const firstOrder = tasks[0]?.myTasksOrderIndex;
          if (firstOrder !== undefined && firstOrder > 0) {
            return firstOrder / 2;
          }
          return 0.5; // Default for beginning
        } else if (index >= tasks.length) {
          // Placed at the end - increment from last item
          const lastOrder = tasks[tasks.length - 1]?.myTasksOrderIndex;
          if (lastOrder !== undefined) {
            return lastOrder + 1.0;
          }
          return tasks.length + 1.0; // Fallback
        } else {
          // Placed between two tasks - use average (fractional indexing)
          const prevTask = tasks[index - 1];
          const nextTask = tasks[index];
          const prevOrder = prevTask?.myTasksOrderIndex;
          const nextOrder = nextTask?.myTasksOrderIndex;
          
          // If both have order indices, use average
          if (prevOrder !== undefined && nextOrder !== undefined) {
            return (prevOrder + nextOrder) / 2;
          }
          
          // Fallback: generate based on position
          return index + 0.5;
        }
      };
      
      // Get the destination tasks for orderIndex calculation
      // When reordering within the same section, we need to account for the task being moved
      let tasksForCalculation = [...(sections[destSection]?.tasks || [])];
      let calculationIndex = destIndex;
      
      if (sourceSection === destSection) {
        // Remove the task being moved from its current position
        tasksForCalculation.splice(sourceIndex, 1);
        
        // CRITICAL: The optimistic UI (line 565) does: sourceTasks.splice(destIndex, 0, task)
        // where sourceTasks ALREADY has the item removed.
        // This means destIndex is where to insert in the array WITHOUT the moved item.
        // We use destIndex directly without adjustment.
        calculationIndex = destIndex;
      }
      
      const newMyTasksOrderIndex = calculateMyTasksOrderIndex(tasksForCalculation, calculationIndex);
      
      // Update the task with new myTasksOrderIndex locally
      task.myTasksOrderIndex = newMyTasksOrderIndex;
      
      // Prepare update data with myTasksOrderIndex and section
      const updateData = { 
        myTasksOrderIndex: newMyTasksOrderIndex,
        myTasksSection: destSection
      };
      
      // Only update status if moving between sections
      if (sourceSection !== destSection) {
        // Map section to status
        const statusMap = {
          pending: 'to_do',
          inProgress: 'in_progress',
          completed: 'done'
        };
        
        const newStatus = statusMap[destSection];
        
        // Determine which user we're updating for (target user in View As mode, current user otherwise)
        const targetUserId = viewAsUserId || user?._id;
        const isCollaborator = task.collaborators?.some(
          c => c.user?._id === targetUserId || c.user === targetUserId
        );
        
        if (isCollaborator) {
          // Update collaborator status for the target user
          const collaboratorIndex = task.collaborators.findIndex(
            c => c.user?._id === targetUserId || c.user === targetUserId
          );
          if (collaboratorIndex !== -1) {
            updateData.collaborators = [...task.collaborators];
            updateData.collaborators[collaboratorIndex] = {
              ...updateData.collaborators[collaboratorIndex],
              status: newStatus
            };
          }
        } else if (task.assignedTo?._id === targetUserId || task.assignedTo === targetUserId) {
          // Update main task status only if target user is assignee and not a collaborator
          task.status = newStatus;
          updateData.status = newStatus;
        }
        
        // Update in the new position with the updated status and myTasksOrderIndex
        setSections(prev => {
          const newSections = { ...prev };
          
          // Update the task in destination
          const destTasks = newSections[destSection].tasks;
          const taskToUpdate = destTasks.find(t => t._id === taskId);
          if (taskToUpdate) {
            if (isCollaborator) {
              // Update collaborator status in UI
              const collabIdx = taskToUpdate.collaborators?.findIndex(
                c => c.user?._id === targetUserId || c.user === targetUserId
              );
              if (collabIdx !== -1 && taskToUpdate.collaborators) {
                taskToUpdate.collaborators[collabIdx].status = newStatus;
              }
            } else {
              taskToUpdate.status = newStatus;
            }
            taskToUpdate.myTasksOrderIndex = newMyTasksOrderIndex;
          }
          
          return newSections;
        });
      }
      
      // Perform API call with myTasksOrderIndex
      // Use View As API if viewing another user's tasks and can manage
      if (viewAsUserId && canManageViewAs) {
        await updateMyTasksOrderAsUser(viewAsUserId, taskId, updateData);
      } else if (!viewAsUserId) {
        // Only update if not in View As mode or if can manage
        await patchTask(taskId, updateData);
      }
      
      // Check if the current user has an active timer for this task
      const hasActiveTimer = task.timeEntries?.some(entry => {
        const entryUserId = entry.user?._id || entry.user;
        return entry.isActive && entryUserId === user?._id;
      });
      
      // Check if task moved to in progress and timer is not running
      if (destSection === 'inProgress' && sourceSection !== 'inProgress') {
        if (!hasActiveTimer) {
          // Show start timer dialog
          setTimerDialog({
            open: true,
            taskId: taskId,
            taskTitle: task.title || 'this task',
            projectName: task.projectId?.name || 'Unknown Project',
            sectionName: task.sectionId?.name || 'No Section'
          });
        }
      }
      
      // Check if task moved FROM in progress to another section with active timer
      if (sourceSection === 'inProgress' && destSection !== 'inProgress') {
        if (hasActiveTimer) {
          // Show stop timer dialog
          setStopTimerDialog({
            open: true,
            taskId: taskId,
            taskTitle: task.title || 'this task',
            projectName: task.projectId?.name || 'Unknown Project',
            sectionName: task.sectionId?.name || 'No Section',
            destSection: destSection
          });
        }
      }
      
      // Show celebration for in progress tasks
      if (destSection === 'inProgress' && sourceSection !== 'inProgress') {
        showInProgressCelebration();
      }
      
      // Show celebration for completed tasks
      if (destSection === 'completed') {
        showCompletionCelebration();
      }
      
      // Show success message for status update (only if not already showing timer prompt)
      if (!(destSection === 'inProgress' && sourceSection !== 'inProgress')) {
        enqueueSnackbar(
          sourceSection !== destSection ? 'Task status updated' : 'Task reordered', 
          { variant: 'success' }
        );
      }
    } catch (error) {
      console.error('Error handling drag:', error);
      enqueueSnackbar('Failed to move task. Reverting changes...', { variant: 'error' });
      
      // Complete rollback using snapshot - simplest and most reliable
      setSections(stateSnapshot.sections);
      
      // Log error details for debugging
      console.error('Drag operation failed - rollback applied', {
        error: error.message,
        taskId,
        sourceSection,
        destSection,
        rollbackTimestamp: Date.now() - stateSnapshot.timestamp
      });
    }
  }, [sections, enqueueSnackbar, showCompletionCelebration, showInProgressCelebration, user, viewAsUserId, canManageViewAs]);
  
  // Handle load more
  const handleLoadMore = useCallback((sectionId) => {
    fetchTasks(true, sectionId);
  }, [fetchTasks]);
  
  // Toggle section collapse
  const toggleSection = useCallback((sectionId) => {
    setSections(prev => ({
      ...prev,
      [sectionId]: {
        ...prev[sectionId],
        collapsed: !prev[sectionId].collapsed
      }
    }));
    
    // Save preference to settings
    const newCollapsedSections = {
      ...dashboardPrefs.collapsedSections,
      [sectionId]: !sections[sectionId].collapsed
    };
    
    updateSettings({
      preferences: {
        ...settings.preferences,
        dashboard: {
          ...dashboardPrefs,
          collapsedSections: newCollapsedSections
        }
      }
    });
  }, [sections, dashboardPrefs, settings.preferences, updateSettings]);
  
  // Handle task click
  const handleTaskClick = useCallback((task) => {
    if (openTaskDrawer) {
      openTaskDrawer(task);
    }
  }, [openTaskDrawer]);
  
  // Check if task is overdue
  const isOverdue = (task) => {
    if (!task.dueDate) return false;
    const now = new Date();
    const dueDate = new Date(task.dueDate);
    return isBefore(dueDate, now) && !isToday(dueDate);
  };
  
  // Check if task is due soon
  const isDueSoon = (task) => {
    if (!task.dueDate) return false;
    const dueDate = new Date(task.dueDate);
    return isToday(dueDate) || (isAfter(dueDate, new Date()) && isBefore(dueDate, addDays(new Date(), 2)));
  };
  
  // Get random encouraging message
  const getRandomMessage = (sectionId) => {
    const messages = ENCOURAGING_MESSAGES[sectionId];
    return messages[Math.floor(Math.random() * messages.length)];
  };
  
  // Render section as column
  const renderSection = (section) => {
    return (
      <Paper 
        key={section.id} 
        sx={{ 
          height: '100%',  // Take full height of parent
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          border: `1px solid ${theme.palette.divider}`,
        }}
      >
        {/* Section Header */}
        <Box
          sx={{
            p: 2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            backgroundColor: theme.palette.action.hover,
            borderBottom: `3px solid ${section.color}`,
            minHeight: 60
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="h6" fontWeight={600}>
              {section.title}
            </Typography>
            <Chip 
              label={section.total} 
              size="small"
              sx={{ 
                backgroundColor: section.color,
                color: 'white',
                fontWeight: 'bold'
              }}
            />
          </Box>
        </Box>
        
        {/* Section Content - Always visible in Kanban view */}
        <Droppable droppableId={section.id} type="TASK">
          {(provided, snapshot) => (
            <Box
              ref={provided.innerRef}
              {...provided.droppableProps}
              sx={{
                flex: 1,
                overflowY: 'auto',  // Vertical scroll when needed
                overflowX: 'hidden', // No horizontal scroll
                p: 2,
                backgroundColor: snapshot.isDraggingOver 
                  ? theme.palette.action.hover 
                  : 'transparent',
                transition: 'background-color 0.2s ease',
              }}
            >
                {section.tasks.length === 0 ? (
                  <Box 
                    sx={{ 
                      py: 4, 
                      textAlign: 'center',
                      color: theme.palette.text.secondary
                    }}
                  >
                    <Typography variant="body1" sx={{ fontStyle: 'italic' }}>
                      {getRandomMessage(section.id)}
                    </Typography>
                  </Box>
                ) : (
                  <Stack spacing={1}>
                    {section.tasks.map((task, index) => (
                      <Box
                        key={task._id}
                        sx={{
                          position: 'relative',
                          '& .MuiPaper-root': {
                            border: isOverdue(task) 
                              ? `2px solid ${theme.palette.error.main}` 
                              : isDueSoon(task)
                              ? `2px solid ${theme.palette.warning.main}`
                              : undefined
                          }
                        }}
                      >
                        {/* Overdue/Due Soon Badge */}
                        {(isOverdue(task) || isDueSoon(task)) && (
                          <Badge
                            badgeContent={
                              isOverdue(task) ? (
                                <WarningIcon sx={{ fontSize: 16 }} />
                              ) : (
                                <ScheduleIcon sx={{ fontSize: 16 }} />
                              )
                            }
                            color={isOverdue(task) ? 'error' : 'warning'}
                            sx={{
                              position: 'absolute',
                              top: 8,
                              right: 8,
                              zIndex: 1
                            }}
                          />
                        )}
                        
                        <UnifiedTaskCard
                          task={task}
                          index={index}
                          viewType="list"
                          showProject={true}
                          compact={false}
                          viewAsUserId={viewAsUserId}
                          onTaskClick={() => handleTaskClick(task)}
                          onStatusChange={async (taskId, newStatus) => {
                            try {
                              await patchTask(taskId, { status: newStatus });
                              // Show celebration if marked as done
                              if (newStatus === 'done') {
                                showCompletionCelebration();
                              }
                              enqueueSnackbar(`Task ${newStatus === 'done' ? 'completed' : 'reopened'}`, { variant: 'success' });
                              // Don't refresh if we have multiple pages loaded
                              // The optimistic update should handle the UI change
                              if (sections.pending.page === 1 && sections.inProgress.page === 1 && sections.completed.page === 1) {
                                fetchTasks();
                              }
                            } catch (error) {
                              console.error('Failed to update task status:', error);
                              enqueueSnackbar('Failed to update task status', { variant: 'error' });
                            }
                          }}
                          onTimeUpdate={() => {
                            // Don't refresh if we have multiple pages loaded
                            if (sections.pending.page === 1 && sections.inProgress.page === 1 && sections.completed.page === 1) {
                              fetchTasks();
                            }
                          }}
                          isDragDisabled={viewAsUserId && !canManageViewAs}
                        />
                      </Box>
                    ))}
                    {provided.placeholder}
                  </Stack>
                )}
                
                {/* Load More Button */}
                {section.hasMore && (
                  <Box sx={{ mt: 2, textAlign: 'center' }}>
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={() => handleLoadMore(section.id)}
                      disabled={section.loading}
                      startIcon={section.loading && <CircularProgress size={16} />}
                    >
                      {section.loading 
                        ? 'Loading...' 
                        : `Load More (${section.total - section.tasks.length} remaining)`}
                    </Button>
                  </Box>
                )}
            </Box>
          )}
        </Droppable>
      </Paper>
    );
  };
  
  // Main render
  if (initialLoading) {
    return (
      <Box 
        sx={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center',
          height: '100%'
        }}
      >
        <CircularProgress />
      </Box>
    );
  }
  
  return (
    <Box sx={{ 
      height: '100%',
      width: '100%',
      maxWidth: '100%',  // Respect parent container width, not viewport width
      display: 'flex', 
      flexDirection: 'column',
      overflowX: 'hidden',  // Prevent horizontal overflow
      overflowY: 'auto',    // Allow vertical overflow for content
      position: 'relative',
      boxSizing: 'border-box'  // Include padding in width calculations
    }}>
      {/* Motivational Message Display */}
      {motivationalMessage.show && (
        <Box
          sx={{
            position: 'fixed',
            top: 80,
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 9999,
            backgroundColor: theme.palette.mode === 'dark' 
              ? theme.palette.grey[900] 
              : theme.palette.background.paper,
            color: theme.palette.secondary.main,
            padding: '16px 24px',
            borderRadius: 2,
            boxShadow: theme.shadows[8],
            border: `2px solid ${theme.palette.secondary.main}`,
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            animation: 'fadeInScale 0.5s ease-out',
            '@keyframes fadeInScale': {
              '0%': {
                opacity: 0,
                transform: 'translateX(-50%) scale(0.8)',
              },
              '100%': {
                opacity: 1,
                transform: 'translateX(-50%) scale(1)',
              },
            },
          }}
        >
          <PlayArrowIcon sx={{ fontSize: 28, color: theme.palette.secondary.main }} />
          <Typography variant="h6" fontWeight={600}>
            {motivationalMessage.text}
          </Typography>
        </Box>
      )}
      
      {/* Header */}
      <Box sx={{ 
        p: { xs: 2, sm: 3 }, 
        borderBottom: 1, 
        borderColor: 'divider',
        flexShrink: 0  // Prevent header from shrinking
      }}>
        <Box sx={{ 
          display: 'flex', 
          flexDirection: { xs: 'column', md: 'row' },
          justifyContent: 'space-between', 
          alignItems: { xs: 'stretch', md: 'flex-start' }, 
          gap: 2,
          mb: 2 
        }}>
          <Box>
            <Typography variant="h4" fontWeight={600}>
              My Tasks {viewAsUserId && '(View As Mode)'}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              {viewAsUserId && !canManageViewAs 
                ? 'View-only mode - You cannot modify tasks'
                : 'Drag tasks between columns to update their status'
              }
            </Typography>
          </Box>
          
          {/* Filter Controls */}
          <Box sx={{ 
            display: 'flex',
            gap: 2,
            flexDirection: { xs: 'column', md: 'row' },
            alignItems: { xs: 'stretch', md: 'center' }
          }}>
            {/* View As Selector - Only show if user has VIEW_AS permission */}
            {canViewAs && (
              <ViewAsSelector
                currentViewAsUser={viewAsUserId}
                onViewAsChange={handleViewAsChange}
                canManage={canManageViewAs}
              />
            )}
            
            {/* Project Filter */}
            <Box sx={{ 
              minWidth: { xs: '100%', md: 240 },
              maxWidth: { xs: '100%', md: 300 }
            }}>
              <MagicSelect
                label="Filter by Project"
                value={selectedProjectId}
                onChange={(value) => setSelectedProjectId(value)}
                options={[
                  { value: 'all', label: 'All Projects', icon: <BusinessIcon /> },
                  ...projects.map(project => ({
                    value: project._id,
                    label: project.name
                  }))
                ]}
                size="small"
                startIcon={<FilterIcon />}
                fullWidth
              />
            </Box>
          </Box>
        </Box>
      </Box>
      
      {/* Task Sections - Horizontal Kanban Layout */}
      <Box sx={{ 
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        minHeight: 0,  // Critical for nested flexbox
        width: '100%',
        maxWidth: '100%',  // Ensure it doesn't exceed viewport width
        overflow: 'hidden'  // Contain the content
      }}>
        <DragDropContext 
          onDragStart={handleDragStart}
          onDragUpdate={handleDragUpdate}
          onDragEnd={handleDragEnd}
        >
          <Box 
            sx={{ 
              display: 'flex',
              gap: 2,
              flex: 1,
              p: 3,
              width: '100%',
              maxWidth: '100%',  // Prevent exceeding container width
              overflowX: 'auto',  // Allow horizontal scroll if needed
              overflowY: 'hidden',  // No vertical scroll on container
              '& > *': {  // Apply to all direct children (the Paper components)
                flex: '1 1 33.333%',  // Each section takes 1/3 of available width
                minWidth: 0,  // Critical: Allow shrinking below content size to prevent expansion
                width: 0,     // Force width calculation from flex-basis
                overflow: 'hidden',  // Prevent content from expanding column width
              }
            }}
          >
            {Object.values(sections).map(renderSection)}
          </Box>
        </DragDropContext>
      </Box>
      
      {/* Timer Start Dialog */}
      <Dialog
        open={timerDialog.open}
        onClose={() => setTimerDialog({ open: false, taskId: null, taskTitle: '', projectName: '', sectionName: '' })}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <TimerIcon color="secondary" />
          Start Timer?
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            You've moved <strong>"{timerDialog.taskTitle}"</strong> to In Progress.
          </DialogContentText>
          
          {/* Project and Section Info */}
          <Box sx={{ 
            mt: 2,
            mb: 2,
            display: 'flex',
            gap: 1,
            flexWrap: 'wrap',
            flex:1,

            alignItems:'center'
          }}>
            <Chip 
              icon={<BusinessIcon />}
              label={timerDialog.projectName}
              size="small"
              variant="outlined"
              // color="primary"
            />
            {timerDialog.sectionName !== 'No Section' && (
              <Chip 
                label={timerDialog.sectionName}
                size="small"
                variant="outlined"
              />
            )}
          </Box>
          
          <DialogContentText>
            Would you like to start tracking time for this task now?
          </DialogContentText>
          
          <Box sx={{ 
            mt: 3, 
            p: 2, 
            bgcolor: theme.palette.mode === 'dark' ? 'background.paper' : 'grey.50',
            borderRadius: 2,
            border: `1px solid ${theme.palette.divider}`
          }}>
            <Typography variant="body2" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <PlayArrowIcon fontSize="small" />
              Starting the timer helps you track time spent on tasks accurately
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button 
            onClick={() => setTimerDialog({ open: false, taskId: null, taskTitle: '', projectName: '', sectionName: '' })}
            variant="outlined"
          >
            Not Now
          </Button>
          <Button 
            onClick={async () => {
              const { taskId } = timerDialog;
              setTimerDialog({ open: false, taskId: null, taskTitle: '', projectName: '', sectionName: '' });
              
              try {
                await startTimer(taskId);
                enqueueSnackbar('Timer started successfully!', { variant: 'success' });
                // Refresh tasks to update timer status
                fetchTasks();
              } catch (error) {
                console.error('Failed to start timer:', error);
                enqueueSnackbar('Failed to start timer', { variant: 'error' });
              }
            }}
            variant="contained"
            startIcon={<PlayArrowIcon />}
            autoFocus
          >
            Start Timer
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Stop Timer Dialog */}
      <Dialog
        open={stopTimerDialog.open}
        onClose={() => setStopTimerDialog({ open: false, taskId: null, taskTitle: '', projectName: '', sectionName: '', destSection: '' })}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, color: theme.palette.warning.main }}>
          <TimerIcon />
          Stop Timer?
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            You have an active timer running for <strong>"{stopTimerDialog.taskTitle}"</strong>.
          </DialogContentText>
          
          {/* Project and Section Info */}
          <Box sx={{ 
            mt: 2,
            mb: 2,
            display: 'flex',
            gap: 1,
            flexWrap: 'wrap'
          }}>
            <Chip 
              icon={<BusinessIcon />}
              label={stopTimerDialog.projectName}
              size="small"
              variant="outlined"
              color="warning"
            />
            {stopTimerDialog.sectionName !== 'No Section' && (
              <Chip 
                label={stopTimerDialog.sectionName}
                size="small"
                variant="outlined"
              />
            )}
          </Box>
          
          <DialogContentText>
            {stopTimerDialog.destSection === 'completed' ? (
              <>Moving this task to "Completed" - would you like to stop the timer and save your tracked time?</>
            ) : (
              <>Moving this task back to "Pending" - would you like to stop the timer and save your tracked time?</>
            )}
          </DialogContentText>
          <Box sx={{ 
            mt: 3, 
            p: 2, 
            bgcolor: theme.palette.mode === 'dark' ? 'background.paper' : 'warning.50',
            borderRadius: 2,
            border: `1px solid ${theme.palette.warning.light}`
          }}>
            <Typography variant="body2" color="text.secondary">
              ⚠️ If you don't stop the timer, it will continue running in the background
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button 
            onClick={() => setStopTimerDialog({ open: false, taskId: null, taskTitle: '', projectName: '', sectionName: '', destSection: '' })}
            variant="outlined"
          >
            Keep Timer Running
          </Button>
          <Button 
            onClick={async () => {
              const { taskId } = stopTimerDialog;
              setStopTimerDialog({ open: false, taskId: null, taskTitle: '', projectName: '', sectionName: '', destSection: '' });
              
              try {
                await stopTimer(taskId);
                enqueueSnackbar('Timer stopped and time saved!', { variant: 'success' });
                // Refresh tasks to update timer status
                fetchTasks();
              } catch (error) {
                console.error('Failed to stop timer:', error);
                enqueueSnackbar('Failed to stop timer', { variant: 'error' });
              }
            }}
            variant="contained"
            color="warning"
            startIcon={<StopIcon />}
            autoFocus
          >
            Stop Timer
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default MyTasksSectioned;